import { createContext, memo, useContext, useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { DialogContainer } from './DialogContainer';
import { DialogContextRef, DialogState, ConfirmDialogConfig, ToastDialogConfig } from './types';
import { registerDialogContext, unregisterDialogContext } from './dialogManager';

const DialogContext = createContext<DialogContextRef | null>(null);

interface DialogProviderProps {
  children: React.ReactNode;
}

const DialogProviderBase = ({ children }: DialogProviderProps) => {
  const [dialogs, setDialogs] = useState<DialogState[]>([]);

  const contextRef: DialogContextRef = useMemo(
    () => ({
      openConfirmDialog: (config: ConfirmDialogConfig) => {
        const id = config.id || uuidv4();

        const dialogState: DialogState = {
          id,
          type: 'confirm',
          config: { ...config, open: true, overlayClassName: config.overlayClassName ?? 'z-401' },
          closeOnConfirm: config.closeOnConfirm ?? true,
        };

        setDialogs((prev) => [...prev, dialogState]);

        return {
          id,
          close: () => contextRef.closeDialog(id),
          update: (config: Partial<ConfirmDialogConfig>) => contextRef.updateDialog(id, config),
          setConfirmLoading: (loading = true) => {
            contextRef.updateDialog(id, {
              confirmButtonProps: {
                ...config.confirmButtonProps,
                loading,
              },
              cancelButtonProps: {
                ...config.cancelButtonProps,
                disabled: loading,
              },
            });
          },
          setCancelLoading: (loading = true) => {
            contextRef.updateDialog(id, {
              cancelButtonProps: {
                ...config.cancelButtonProps,
                loading,
              },
              confirmButtonProps: {
                ...config.confirmButtonProps,
                disabled: loading,
              },
            });
          },
        };
      },

      openToastDialog: (config: ToastDialogConfig) => {
        const id = config.id || uuidv4();

        const dialogState: DialogState = {
          id,
          type: 'toast',
          config: { ...config, open: true, overlayClassName: config.overlayClassName ?? 'z-401' },
          closeOnConfirm: config.closeOnConfirm ?? true,
        };

        setDialogs((prev) => [...prev, dialogState]);

        return {
          id,
          close: () => contextRef.closeDialog(id),
          update: (config: Partial<ToastDialogConfig>) => contextRef.updateDialog(id, config),
        };
      },

      closeDialog: (id: string) => {
        setDialogs((prev) =>
          prev.map((dialog) => (dialog.id === id ? { ...dialog, config: { ...dialog.config, open: false } } : dialog)),
        );

        setTimeout(() => {
          setDialogs((prev) => prev.filter((dialog) => dialog.id !== id));
        }, 200);
      },

      closeAllDialogs: () => {
        setDialogs([]);
      },

      updateDialog: (id: string, config: Partial<ConfirmDialogConfig | ToastDialogConfig>) => {
        setDialogs((prev) =>
          prev.map((dialog) => (dialog.id === id ? { ...dialog, config: { ...dialog.config, ...config } } : dialog)),
        );
      },
    }),
    [],
  );

  useEffect(() => {
    registerDialogContext(contextRef);

    return () => unregisterDialogContext();
  }, [contextRef]);

  const handleCloseDialog = (id: string) => {
    contextRef.closeDialog(id);
  };

  return (
    <DialogContext.Provider value={contextRef}>
      {children}
      <DialogContainer dialogs={dialogs} onClose={handleCloseDialog} />
    </DialogContext.Provider>
  );
};

export const DialogProvider = memo(DialogProviderBase);

// Hook for components that need direct access to the context (optional)
export const useDialogContext = (): DialogContextRef => {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error('useDialogContext must be used within a DialogProvider');
  }
  return context;
};
